import React, { useState, useEffect } from 'react';
import { formatNumber, formatKcal, formatPercentage } from '../utils/numberFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Plus, ArrowLeft, Loader2, X, Edit, Trash2, ChevronDown, ChevronRight, Beaker, FileSpreadsheet, FileDown, FileUp, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { parenteralAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import ConfirmDialog, { AlertDialog } from '../components/ConfirmDialog';
import { useConfirmDialog, useAlertDialog } from '../hooks/useDialog';
import BackToTop from '../components/BackToTop';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { exportParenteralToExcel, downloadParenteralTemplateExcel, parseParenteralExcel } from '../utils/databaseExcelHelper';

export default function ParenteralDatabase() {
  const navigate = useNavigate();
  const { dialog: confirmDialog, confirm, close: closeConfirm } = useConfirmDialog();
  const { dialog: alertDialog, alert: showAlert, close: closeAlert } = useAlertDialog();
  
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Detail editor state (factors, electrolytes, other)
  const [showDetailEditor, setShowDetailEditor] = useState(false);
  const [editingDetailProduct, setEditingDetailProduct] = useState(null);
  const [detailData, setDetailData] = useState({
    factors: {},
    electrolytes: {},
    other: {}
  });
  const [savingDetail, setSavingDetail] = useState(false);
  
  // Excel Import state for Admin
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [parsedImportData, setParsedImportData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const [newProduct, setNewProduct] = useState({
    name: '',
    unit: '500ml',
    energy: '',
    protein: '',
    carbs: '',
    fat: '',
    // Required factors
    carb_factor: '',
    fat_factor: '',
    // Optional electrolytes (default 0)
    sodium: '0',
    potassium: '0',
    calcium: '0',
    chloride: '0',
    magnesium: '0',
    phosphate: '0',
    // Optional other values (default 0)
    lactate: '0',
    acetate: '0',
    nitrogen: '0',
  });
  
  const [editProduct, setEditProduct] = useState({
    name: '',
    unit: '',
    energy: '',
    protein: '',
    carbs: '',
    fat: '',
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [search, products]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await parenteralAPI.getAll();
      setProducts(data);
      setFilteredProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to load products. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomProduct = async () => {
    // Validate required fields
    if (!newProduct.name || !newProduct.energy || !newProduct.carb_factor || !newProduct.fat_factor) {
      showAlert({
        type: 'warning',
        title: 'Missing Information',
        message: 'Please enter name, energy, carb factor, and fat factor (required fields)'
      });
      return;
    }

    setAdding(true);
    try {
      await parenteralAPI.create({
        name: newProduct.name,
        unit: newProduct.unit,
        macronutrients: {
          energy_kcal: parseFloat(newProduct.energy),
          protein_g: parseFloat(newProduct.protein || 0),
          carbohydrate_g: parseFloat(newProduct.carbs || 0),
          fat_g: parseFloat(newProduct.fat || 0),
        },
        factors: {
          carb_factor: parseFloat(newProduct.carb_factor),
          fat_factor: parseFloat(newProduct.fat_factor),
        },
        electrolytes: {
          sodium_mmol: parseFloat(newProduct.sodium || 0),
          potassium_mmol: parseFloat(newProduct.potassium || 0),
          calcium_mmol: parseFloat(newProduct.calcium || 0),
          chloride_mmol: parseFloat(newProduct.chloride || 0),
          magnesium_mmol: parseFloat(newProduct.magnesium || 0),
          phosphate_mmol: parseFloat(newProduct.phosphate || 0),
        },
        other: {
          lactate_mmol: parseFloat(newProduct.lactate || 0),
          acetate_mmol: parseFloat(newProduct.acetate || 0),
          nitrogen_g: parseFloat(newProduct.nitrogen || 0),
        },
      });

      showAlert({
        type: 'success',
        title: 'Success',
        message: 'Custom product added successfully!'
      });
      setShowAddDialog(false);
      setNewProduct({ 
        name: '', unit: '500ml', energy: '', protein: '', carbs: '', fat: '',
        carb_factor: '', fat_factor: '',
        sodium: '0', potassium: '0', calcium: '0', chloride: '0', magnesium: '0', phosphate: '0',
        lactate: '0', acetate: '0', nitrogen: '0'
      });
      loadProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to add custom product'
      });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (productId, productName, isCustom) => {
    // Admin can delete any product, non-admin can only delete custom products
    if (!isAdmin && !isCustom) {
      showAlert({
        type: 'warning',
        title: 'Cannot Delete',
        message: 'Cannot delete system products. Only custom products can be deleted.'
      });
      return;
    }

    confirm({
      type: 'danger',
      title: 'Delete Product',
      message: `Delete "${productName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await parenteralAPI.delete(productId);
          showAlert({
            type: 'success',
            title: 'Deleted',
            message: 'Product deleted successfully'
          });
          loadProducts();
        } catch (error) {
          console.error('Error deleting product:', error);
          showAlert({
            type: 'danger',
            title: 'Error',
            message: 'Failed to delete product'
          });
        }
      }
    });
  };
  
  const handleEdit = (product) => {
    setEditingProduct(product);
    setEditProduct({
      name: product.name,
      unit: product.unit,
      energy: product.macronutrients.energy_kcal.toString(),
      protein: product.macronutrients.protein_g.toString(),
      carbs: product.macronutrients.carbohydrate_g.toString(),
      fat: product.macronutrients.fat_g.toString(),
    });
    setShowEditDialog(true);
  };
  
  const handleUpdateProduct = async () => {
    if (!editProduct.name || !editProduct.energy) {
      showAlert({
        type: 'warning',
        title: 'Missing Information',
        message: 'Please enter at least name and energy'
      });
      return;
    }

    setEditing(true);
    try {
      await parenteralAPI.update(editingProduct.id, {
        name: editProduct.name,
        unit: editProduct.unit,
        macronutrients: {
          energy_kcal: parseFloat(editProduct.energy),
          protein_g: parseFloat(editProduct.protein || 0),
          carbohydrate_g: parseFloat(editProduct.carbs || 0),
          fat_g: parseFloat(editProduct.fat || 0),
        },
      });

      showAlert({
        type: 'success',
        title: 'Success',
        message: 'Product updated successfully!'
      });
      setShowEditDialog(false);
      setEditingProduct(null);
      setEditProduct({ name: '', unit: '', energy: '', protein: '', carbs: '', fat: '' });
      loadProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to update product'
      });
    } finally {
      setEditing(false);
    }
  };
  
  const toggleExpand = (productId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedRows(newExpanded);
  };
  
  const openDetailEditor = (product) => {
    setEditingDetailProduct(product);
    setDetailData({
      factors: product.factors || {},
      electrolytes: product.electrolytes || {},
      other: product.other || {}
    });
    setShowDetailEditor(true);
  };
  
  const handleSaveDetails = async () => {
    if (!editingDetailProduct) return;
    
    setSavingDetail(true);
    try {
      await parenteralAPI.update(editingDetailProduct.id, {
        name: editingDetailProduct.name,
        unit: editingDetailProduct.unit,
        macronutrients: editingDetailProduct.macronutrients,
        factors: detailData.factors,
        electrolytes: detailData.electrolytes,
        other: detailData.other,
      });
      
      showAlert({
        type: 'success',
        title: 'Success',
        message: 'Product details updated successfully!'
      });
      
      setShowDetailEditor(false);
      await loadProducts();
    } catch (error) {
      console.error('Error updating details:', error);
      showAlert({
        type: 'danger',
        title: 'Error',
        message: 'Failed to update product details'
      });
    } finally {
      setSavingDetail(false);
    }
  };
  
  const DetailPanel = ({ product }) => {
    const { factors = {}, electrolytes = {}, other = {} } = product;
    
    return (
      <tr className="bg-blue-50/50">
        <td colSpan="8" className="px-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Factors */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Factors
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Carb Factor:</span><span className="font-medium">{formatNumber(factors.carb_factor || 0)}</span></div>
                <div className="flex justify-between"><span>Fat Factor:</span><span className="font-medium">{formatNumber(factors.fat_factor || 0)}</span></div>
              </div>
            </div>
            
            {/* Electrolytes */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Electrolytes
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Sodium (Na):</span><span className="font-medium">{formatNumber(electrolytes.sodium_mmol || 0)} mmol</span></div>
                <div className="flex justify-between"><span>Potassium (K):</span><span className="font-medium">{formatNumber(electrolytes.potassium_mmol || 0)} mmol</span></div>
                <div className="flex justify-between"><span>Calcium (Ca):</span><span className="font-medium">{formatNumber(electrolytes.calcium_mmol || 0)} mmol</span></div>
                <div className="flex justify-between"><span>Chloride (Cl):</span><span className="font-medium">{formatNumber(electrolytes.chloride_mmol || 0)} mmol</span></div>
                <div className="flex justify-between"><span>Magnesium (Mg):</span><span className="font-medium">{formatNumber(electrolytes.magnesium_mmol || 0)} mmol</span></div>
                <div className="flex justify-between"><span>Phosphate (PO4):</span><span className="font-medium">{formatNumber(electrolytes.phosphate_mmol || 0)} mmol</span></div>
              </div>
            </div>
            
            {/* Other Values */}
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                Other Values
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Lactate:</span><span className="font-medium">{formatNumber(other.lactate_mmol || 0)} mmol</span></div>
                <div className="flex justify-between"><span>Acetate:</span><span className="font-medium">{formatNumber(other.acetate_meq || 0)} mEq</span></div>
                <div className="flex justify-between"><span>Nitrogen:</span><span className="font-medium">{formatNumber(other.nitrogen_g || 0)} g</span></div>
                {other.osmolarity > 0 && <div className="flex justify-between"><span>Osmolarity:</span><span className="font-medium">{formatNumber(other.osmolarity || 0, 0)} mOsm/L</span></div>}
                {other.mct > 0 && <div className="flex justify-between"><span>MCT:</span><span className="font-medium">{formatNumber(other.mct || 0)}</span></div>}
                {other.omega3 > 0 && <div className="flex justify-between"><span>Omega-3:</span><span className="font-medium">{formatNumber(other.omega3 || 0)}</span></div>}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Excel Handlers for Admin
  const handleExportExcel = () => {
    try {
      exportParenteralToExcel(products);
      showAlert({
        type: 'success',
        title: 'Berhasil Ekspor',
        message: `Berhasil mengekspor ${products.length} produk parenteral ke file Excel.`
      });
    } catch (err) {
      showAlert({
        type: 'danger',
        title: 'Gagal Ekspor',
        message: err.message || 'Terjadi kesalahan saat mengekspor Excel'
      });
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadParenteralTemplateExcel();
    } catch (err) {
      showAlert({
        type: 'danger',
        title: 'Gagal Unduh Template',
        message: err.message || 'Terjadi kesalahan saat mengunduh template Excel'
      });
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportError('');
    try {
      const result = await parseParenteralExcel(file);
      setParsedImportData(result.items);
    } catch (err) {
      setImportError(err.message || 'Gagal membaca file Excel');
      setParsedImportData(null);
    }
  };

  const handleExecuteImport = async () => {
    if (!parsedImportData || parsedImportData.length === 0) return;
    setImporting(true);
    try {
      const res = await parenteralAPI.bulkImport(parsedImportData);
      showAlert({
        type: 'success',
        title: 'Import Berhasil',
        message: res.message || `Berhasil mengimpor ${parsedImportData.length} produk parenteral ke database.`
      });
      setShowImportDialog(false);
      setImportFile(null);
      setParsedImportData(null);
      await loadProducts();
    } catch (err) {
      showAlert({
        type: 'danger',
        title: 'Gagal Import',
        message: err.response?.data?.detail || err.message || 'Terjadi kesalahan saat menyimpan ke database'
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between">
      <div>
        <Navbar 
          user={user} 
          onLogout={() => { localStorage.removeItem('token'); navigate('/'); }} 
          breadcrumbs={[{ label: 'Database Parenteral' }]} 
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Database Parenteral</h1>
              <p className="text-sm text-slate-500 mt-1">
                Tersedia {filteredProducts.length} dari {products.length} produk cairan nutrisi parenteral.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              {isAdmin && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadTemplate}
                    className="border-slate-200 text-slate-700 hover:bg-slate-100/80 text-xs sm:text-sm shadow-2xs"
                    title="Unduh format template Excel untuk import data parenteral"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-1.5 text-sky-600" />
                    <span>Template Excel</span>
                  </Button>

                  <Button 
                    variant="outline" 
                    onClick={handleExportExcel}
                    className="border-slate-200 text-slate-700 hover:bg-slate-100/80 text-xs sm:text-sm shadow-2xs"
                    title="Ekspor seluruh database parenteral ke file Excel"
                  >
                    <FileDown className="h-4 w-4 mr-1.5 text-sky-600" />
                    <span>Export Excel</span>
                  </Button>

                  <Button 
                    variant="outline" 
                    onClick={() => setShowImportDialog(true)}
                    className="border-sky-300 bg-sky-50/50 text-sky-700 hover:bg-sky-100/80 text-xs sm:text-sm shadow-2xs"
                    title="Impor data produk parenteral dari file Excel ke database"
                  >
                    <FileUp className="h-4 w-4 mr-1.5 text-sky-600" />
                    <span>Import Excel</span>
                  </Button>
                </>
              )}

              <Button 
                onClick={() => setShowAddDialog(true)} 
                className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white shadow-xs text-xs sm:text-sm"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                <span>Tambah Produk</span>
              </Button>
            </div>
          </div>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle>Search Products</CardTitle>
            <CardDescription>Search from 61+ parenteral products</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-accent-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading products...</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50/90 border-b border-slate-200 text-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold w-10"></th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Product Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Unit</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Energy (Kcal)</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Protein (g)</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Carbs (g)</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Fat (g)</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Type</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <React.Fragment key={product.id}>
                          <tr className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => toggleExpand(product.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                title="Show details"
                              >
                                {expandedRows.has(product.id) ? (
                                  <ChevronDown className="h-4 w-4 text-sky-600" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-400" />
                                )}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">{product.name}</td>
                            <td className="px-4 py-3 text-sm text-slate-600">{product.unit}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-slate-800">{Math.round(product.macronutrients.energy_kcal)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-700">{formatNumber(product.macronutrients.protein_g)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-700">{formatNumber(product.macronutrients.carbohydrate_g)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-700">{formatNumber(product.macronutrients.fat_g)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                                product.is_custom ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-slate-100 text-slate-600 border-slate-200'
                              }`}>
                                {product.is_custom ? 'Custom' : 'System'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {isAdmin && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleEdit(product)}
                                      title="Edit macronutrients"
                                    >
                                      <Edit className="h-4 w-4 text-blue-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openDetailEditor(product)}
                                      title="Edit details (factors, electrolytes, other)"
                                    >
                                      <Beaker className="h-4 w-4 text-purple-600" />
                                    </Button>
                                  </>
                                )}
                                {(isAdmin || product.is_custom) && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelete(product.id, product.name, product.is_custom)}
                                    title="Delete product"
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedRows.has(product.id) && <DetailPanel product={product} />}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3 p-4">
                  {filteredProducts.map((product) => (
                    <div key={product.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                      {/* Main Card */}
                      <div className="p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">{product.name}</p>
                            <p className="text-xs text-gray-500">{product.unit}</p>
                          </div>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ml-2 flex-shrink-0 ${
                            product.is_custom ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-slate-100 text-slate-600 border-slate-200'
                          }`}>
                            {product.is_custom ? 'Custom' : 'System'}
                          </span>
                        </div>
                        
                        {/* Macronutrients Grid */}
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          <div className="bg-amber-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Energy:</span>
                            <span className="font-semibold ml-1">{Math.round(product.macronutrients.energy_kcal)} Kcal</span>
                          </div>
                          <div className="bg-blue-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Protein:</span>
                            <span className="font-semibold ml-1">{formatNumber(product.macronutrients.protein_g)}g</span>
                          </div>
                          <div className="bg-green-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Carbs:</span>
                            <span className="font-semibold ml-1">{formatNumber(product.macronutrients.carbohydrate_g)}g</span>
                          </div>
                          <div className="bg-red-50 px-2 py-1 rounded">
                            <span className="text-gray-600">Fat:</span>
                            <span className="font-semibold ml-1">{formatNumber(product.macronutrients.fat_g)}g</span>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => toggleExpand(product.id)}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {expandedRows.has(product.id) ? (
                              <>
                                <ChevronDown className="h-4 w-4" />
                                Hide Details
                              </>
                            ) : (
                              <>
                                <ChevronRight className="h-4 w-4" />
                                Show Details
                              </>
                            )}
                          </button>
                          
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => handleEdit(product)}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 ml-auto"
                                title="Edit macronutrients"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openDetailEditor(product)}
                                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
                                title="Edit details"
                              >
                                <Beaker className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {(isAdmin || product.is_custom) && (
                            <button
                              onClick={() => handleDelete(product.id, product.name, product.is_custom)}
                              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                              title="Delete product"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Detail Panel */}
                      {expandedRows.has(product.id) && (
                        <div className="border-t border-gray-200 bg-blue-50/30 p-3">
                          <DetailPanel product={product} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {filteredProducts.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No products found
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Custom Product Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Custom Product</DialogTitle>
            <DialogDescription>Add your own parenteral product to the database</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Product Name *</Label>
                  <Input
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    placeholder="e.g., Custom TPN 1000ml"
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input
                    value={newProduct.unit}
                    onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })}
                    placeholder="e.g., 500ml, 1000ml"
                  />
                </div>
              </div>
            </div>

            {/* Macronutrients - Required */}
            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold text-sm text-red-600">Macronutrients (Required *)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Energy (Kcal) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.energy}
                    onChange={(e) => setNewProduct({ ...newProduct, energy: e.target.value })}
                    placeholder="200"
                  />
                </div>
                <div>
                  <Label>Protein (g) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.protein}
                    onChange={(e) => setNewProduct({ ...newProduct, protein: e.target.value })}
                    placeholder="25"
                  />
                </div>
                <div>
                  <Label>Carbs (g) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.carbs}
                    onChange={(e) => setNewProduct({ ...newProduct, carbs: e.target.value })}
                    placeholder="50"
                  />
                </div>
                <div>
                  <Label>Fat (g) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.fat}
                    onChange={(e) => setNewProduct({ ...newProduct, fat: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Factors - Required */}
            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold text-sm text-red-600">Factors (Required *)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Carb Factor *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newProduct.carb_factor}
                    onChange={(e) => setNewProduct({ ...newProduct, carb_factor: e.target.value })}
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <Label>Fat Factor *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newProduct.fat_factor}
                    onChange={(e) => setNewProduct({ ...newProduct, fat_factor: e.target.value })}
                    placeholder="0.0"
                  />
                </div>
              </div>
            </div>

            {/* Electrolytes - Optional */}
            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold text-sm">Electrolytes (Optional - default 0)</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Na (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.sodium}
                    onChange={(e) => setNewProduct({ ...newProduct, sodium: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>K (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.potassium}
                    onChange={(e) => setNewProduct({ ...newProduct, potassium: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Ca (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.calcium}
                    onChange={(e) => setNewProduct({ ...newProduct, calcium: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Cl (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.chloride}
                    onChange={(e) => setNewProduct({ ...newProduct, chloride: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Mg (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.magnesium}
                    onChange={(e) => setNewProduct({ ...newProduct, magnesium: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>PO4 (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.phosphate}
                    onChange={(e) => setNewProduct({ ...newProduct, phosphate: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Other Values - Optional */}
            <div className="space-y-3 border-t pt-4">
              <h3 className="font-semibold text-sm">Other (Optional - default 0)</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Lactate (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.lactate}
                    onChange={(e) => setNewProduct({ ...newProduct, lactate: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Acetate (mmol)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.acetate}
                    onChange={(e) => setNewProduct({ ...newProduct, acetate: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Nitrogen (g)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newProduct.nitrogen}
                    onChange={(e) => setNewProduct({ ...newProduct, nitrogen: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground pt-2">
              * = Required fields. Other fields will default to 0 if not filled.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCustomProduct} disabled={adding}>
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
            <DialogDescription>Update parenteral product information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product Name *</Label>
              <Input
                value={editProduct.name}
                onChange={(e) => setEditProduct({ ...editProduct, name: e.target.value })}
                placeholder="e.g., Custom TPN 1000ml"
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Input
                value={editProduct.unit}
                onChange={(e) => setEditProduct({ ...editProduct, unit: e.target.value })}
                placeholder="e.g., 500ml, 1000ml"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Energy (Kcal) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editProduct.energy}
                  onChange={(e) => setEditProduct({ ...editProduct, energy: e.target.value })}
                  placeholder="200"
                />
              </div>
              <div>
                <Label>Protein (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editProduct.protein}
                  onChange={(e) => setEditProduct({ ...editProduct, protein: e.target.value })}
                  placeholder="25"
                />
              </div>
              <div>
                <Label>Carbs (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editProduct.carbs}
                  onChange={(e) => setEditProduct({ ...editProduct, carbs: e.target.value })}
                  placeholder="50"
                />
              </div>
              <div>
                <Label>Fat (g)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={editProduct.fat}
                  onChange={(e) => setEditProduct({ ...editProduct, fat: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateProduct} disabled={editing}>
              {editing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
        buttonText={alertDialog.buttonText}
      />
      
      {/* Detail Editor Modal (Factors, Electrolytes, Other) */}
      {showDetailEditor && editingDetailProduct && (
        <Dialog open={showDetailEditor} onOpenChange={setShowDetailEditor}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Details - {editingDetailProduct.name}</DialogTitle>
              <DialogDescription>Update factors, electrolytes, and other values (per {editingDetailProduct.unit})</DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
              {/* Factors Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                  Factors
                </h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Carb Factor</Label>
                    <Input type="number" step="0.01" value={detailData.factors.carb_factor || 0} onChange={(e) => setDetailData({...detailData, factors: {...detailData.factors, carb_factor: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Fat Factor</Label>
                    <Input type="number" step="0.01" value={detailData.factors.fat_factor || 0} onChange={(e) => setDetailData({...detailData, factors: {...detailData.factors, fat_factor: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                </div>
              </div>
              
              {/* Electrolytes Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  Electrolytes
                </h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Sodium (mmol)</Label>
                    <Input type="number" step="0.1" value={detailData.electrolytes.sodium_mmol || 0} onChange={(e) => setDetailData({...detailData, electrolytes: {...detailData.electrolytes, sodium_mmol: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Potassium (mmol)</Label>
                    <Input type="number" step="0.1" value={detailData.electrolytes.potassium_mmol || 0} onChange={(e) => setDetailData({...detailData, electrolytes: {...detailData.electrolytes, potassium_mmol: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Calcium (mmol)</Label>
                    <Input type="number" step="0.1" value={detailData.electrolytes.calcium_mmol || 0} onChange={(e) => setDetailData({...detailData, electrolytes: {...detailData.electrolytes, calcium_mmol: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Chloride (mmol)</Label>
                    <Input type="number" step="0.1" value={detailData.electrolytes.chloride_mmol || 0} onChange={(e) => setDetailData({...detailData, electrolytes: {...detailData.electrolytes, chloride_mmol: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Magnesium (mmol)</Label>
                    <Input type="number" step="0.1" value={detailData.electrolytes.magnesium_mmol || 0} onChange={(e) => setDetailData({...detailData, electrolytes: {...detailData.electrolytes, magnesium_mmol: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Phosphate (mmol)</Label>
                    <Input type="number" step="0.1" value={detailData.electrolytes.phosphate_mmol || 0} onChange={(e) => setDetailData({...detailData, electrolytes: {...detailData.electrolytes, phosphate_mmol: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                </div>
              </div>
              
              {/* Other Values Section */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                  Other Values
                </h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Lactate (mmol)</Label>
                    <Input type="number" step="0.1" value={detailData.other.lactate_mmol || 0} onChange={(e) => setDetailData({...detailData, other: {...detailData.other, lactate_mmol: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Acetate (mEq)</Label>
                    <Input type="number" step="0.1" value={detailData.other.acetate_meq || 0} onChange={(e) => setDetailData({...detailData, other: {...detailData.other, acetate_meq: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Nitrogen (g)</Label>
                    <Input type="number" step="0.1" value={detailData.other.nitrogen_g || 0} onChange={(e) => setDetailData({...detailData, other: {...detailData.other, nitrogen_g: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Osmolarity (mOsm/L)</Label>
                    <Input type="number" step="1" value={detailData.other.osmolarity || 0} onChange={(e) => setDetailData({...detailData, other: {...detailData.other, osmolarity: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">MCT</Label>
                    <Input type="number" step="0.1" value={detailData.other.mct || 0} onChange={(e) => setDetailData({...detailData, other: {...detailData.other, mct: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Omega-3</Label>
                    <Input type="number" step="0.1" value={detailData.other.omega3 || 0} onChange={(e) => setDetailData({...detailData, other: {...detailData.other, omega3: parseFloat(e.target.value) || 0}})} className="h-8 text-sm" />
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailEditor(false)} disabled={savingDetail}>
                Cancel
              </Button>
              <Button onClick={handleSaveDetails} disabled={savingDetail} className="bg-blue-600 hover:bg-blue-700">
                {savingDetail ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Details'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Excel Import Dialog for Admin */}
      {isAdmin && (
        <Dialog open={showImportDialog} onOpenChange={(open) => {
          setShowImportDialog(open);
          if (!open) {
            setImportFile(null);
            setParsedImportData(null);
            setImportError('');
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-900">
                <FileUp className="h-5 w-5 text-sky-600" />
                <span>Impor Database Parenteral dari Excel</span>
              </DialogTitle>
              <DialogDescription>
                Unggah file Excel (.xlsx) sesuai template produk parenteral NUTRIMUS. Produk yang diimpor akan menjadi master data sistem.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* File upload area */}
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-sky-400 transition-colors bg-slate-50/50">
                <input 
                  type="file" 
                  accept=".xlsx, .xls" 
                  onChange={handleFileChange} 
                  id="excel-parenteral-file-input"
                  className="hidden" 
                />
                <label htmlFor="excel-parenteral-file-input" className="cursor-pointer block">
                  <div className="w-12 h-12 mx-auto rounded-full bg-sky-50 text-sky-600 flex items-center justify-center mb-3">
                    <Upload className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">
                    {importFile ? importFile.name : 'Pilih File Excel (.xlsx)'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {importFile ? `${(importFile.size / 1024).toFixed(1)} KB` : 'Klik untuk mencari file atau seret file ke sini'}
                  </p>
                </label>
              </div>

              {/* Error message */}
              {importError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-semibold">Kesalahan membaca file:</strong> {importError}
                  </div>
                </div>
              )}

              {/* Preview parsed data */}
              {parsedImportData && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-sky-700 flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>{parsedImportData.length} produk parenteral valid terdeteksi</span>
                    </span>
                    <span className="text-slate-400">Pratinjau 5 data pertama:</span>
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-x-auto text-xs max-h-48">
                    <table className="w-full">
                      <thead className="bg-slate-100 text-slate-700 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Nama Produk</th>
                          <th className="px-3 py-2 text-left">Kemasan</th>
                          <th className="px-3 py-2 text-right">Energi</th>
                          <th className="px-3 py-2 text-right">Asam Amino</th>
                          <th className="px-3 py-2 text-right">Dextrose</th>
                          <th className="px-3 py-2 text-right">Lipid</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {parsedImportData.slice(0, 5).map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-3 py-1.5 font-medium text-slate-900">{item.name}</td>
                            <td className="px-3 py-1.5 text-slate-600">{item.unit}</td>
                            <td className="px-3 py-1.5 text-right font-semibold">{item.macronutrients.energy_kcal} Kcal</td>
                            <td className="px-3 py-1.5 text-right">{item.macronutrients.protein_g}g</td>
                            <td className="px-3 py-1.5 text-right">{item.macronutrients.carbohydrate_g}g</td>
                            <td className="px-3 py-1.5 text-right">{item.macronutrients.fat_g}g</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportDialog(false);
                  setImportFile(null);
                  setParsedImportData(null);
                  setImportError('');
                }}
                disabled={importing}
              >
                Batal
              </Button>
              <Button
                onClick={handleExecuteImport}
                disabled={!parsedImportData || parsedImportData.length === 0 || importing}
                className="bg-sky-600 hover:bg-sky-700 text-white"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span>Menyimpan ke Database...</span>
                  </>
                ) : (
                  <>
                    <FileUp className="h-4 w-4 mr-2" />
                    <span>Mulai Impor ({parsedImportData ? parsedImportData.length : 0} Produk)</span>
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      </div>
      
      <Footer />
      {/* Back to Top Button */}
      <BackToTop />
    </div>
  );
}
